(function () {
  const CART_THRESHOLD = 2;
  let lastBundleStateKey = null;
  let currentProduct = null;
  const viewedAccordions = new WeakSet();
  const viewedTwiniHosts = new WeakSet();
  const twiniInteractionKeys = new Set();

  const hasPostHog = () => window.posthog && typeof window.posthog.capture === 'function';

  const capture = (eventName, properties = {}) => {
    if (!hasPostHog()) return;
    window.posthog.capture(eventName, properties);
  };

  const money = (cents) => {
    const value = Number(cents);
    return Number.isFinite(value) ? value / 100 : 0;
  };

  const cartCurrency = (cart) => cart?.currency || window.Shopify?.currency?.active || undefined;

  const asBool = (value) => value === true || value === 'true';

  const productProps = () => {
    if (!currentProduct) return {};

    return {
      product_id: currentProduct.product_id,
      variant_id: currentProduct.variant_id,
      product_title: currentProduct.product_title,
      product_handle: currentProduct.product_handle,
      currency: currentProduct.currency,
    };
  };

  const getCart = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  };

  const findCartLine = (cart, variantId) => {
    if (!cart?.items?.length || !variantId) return null;
    return cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;
  };

  const normalizeAddedItem = (addedItem = {}, cart = null) => {
    const variantId = addedItem.variant_id || addedItem.id;
    const cartLine = findCartLine(cart, variantId);
    const item = cartLine || addedItem;

    return {
      product_id: item.product_id ? String(item.product_id) : undefined,
      variant_id: variantId ? String(variantId) : undefined,
      product_title: item.product_title || item.title || addedItem.product_title,
      variant_title: item.variant_title || addedItem.variant_title,
      price: money(item.price || addedItem.price),
      quantity: Number(addedItem.quantity || item.quantity || 1),
    };
  };

  const trackProductViewed = (product) => {
    if (!product || product.__tracked) return;
    product.__tracked = true;
    currentProduct = product;
    capture('product_viewed', product);
  };

  const accordionProps = (row) => {
    const trigger = row.querySelector('[data-nola-disclosure-trigger]');

    return {
      ...productProps(),
      accordion_title: row.dataset.accordionTitle || trigger?.textContent?.trim() || 'Section',
      accordion_index: Number(row.dataset.accordionIndex) || undefined,
      is_open: trigger?.getAttribute('aria-expanded') === 'true' || row.classList.contains('is-open'),
      is_locked_open: asBool(row.dataset.accordionLockedOpen),
      has_product_description: asBool(row.dataset.accordionHasDescription),
      has_twini: asBool(row.dataset.accordionHasTwini),
    };
  };

  const trackPdpAccordionViewed = (row) => {
    if (!row || viewedAccordions.has(row)) return;
    viewedAccordions.add(row);
    capture('pdp_accordion_viewed', accordionProps(row));
  };

  const trackPdpAccordionToggled = (row) => {
    if (!row) return;
    capture('pdp_accordion_toggled', accordionProps(row));
  };

  const trackTwiniViewed = (host) => {
    if (!host || viewedTwiniHosts.has(host)) return;
    viewedTwiniHosts.add(host);
    const row = host.closest('[data-nola-pdp-accordion]');
    capture('twini_product_chat_viewed', {
      ...productProps(),
      accordion_title: row?.dataset.accordionTitle || 'Product',
    });
  };

  const trackTwiniInteracted = (host, interactionType, target) => {
    if (!host) return;
    const row = host.closest('[data-nola-pdp-accordion]');
    const key = `${interactionType}:${row?.dataset.accordionTitle || 'Product'}`;
    if (interactionType !== 'submit' && twiniInteractionKeys.has(key)) return;
    twiniInteractionKeys.add(key);

    capture('twini_product_chat_interacted', {
      ...productProps(),
      accordion_title: row?.dataset.accordionTitle || 'Product',
      interaction_type: interactionType,
      target_tag: target?.tagName?.toLowerCase(),
      target_type: target?.getAttribute?.('type') || undefined,
      target_role: target?.getAttribute?.('role') || undefined,
    });
  };

  const trackAddToCart = async ({ addedItem, cart, addedFrom = 'unknown' } = {}) => {
    try {
      const nextCart = cart || await getCart();
      const itemProps = normalizeAddedItem(addedItem, nextCart);
      capture('add_to_cart', {
        ...itemProps,
        cart_total_after: money(nextCart.total_price),
        cart_item_count_after: nextCart.item_count,
        cart_unique_items_after: nextCart.items?.length || 0,
        has_bundle_discount: Number(nextCart.item_count) >= CART_THRESHOLD,
        currency: cartCurrency(nextCart),
        added_from: addedFrom,
      });
      trackBundleThresholdState(nextCart, { force: false });
    } catch (error) {
      console.warn('[nola-posthog] add_to_cart tracking failed', error);
    }
  };

  const bundleStateForCart = (cart) => {
    const itemCount = Number(cart?.item_count) || 0;
    if (itemCount >= CART_THRESHOLD) return 'unlocked_2_plus';
    if (itemCount === 1) return 'progress_1_item';
    return 'empty';
  };

  const trackBundleThresholdState = (cart, { force = false } = {}) => {
    if (!cart) return;
    const state = bundleStateForCart(cart);
    const itemCount = Number(cart.item_count) || 0;
    const key = `${state}:${itemCount}:${cart.total_price}:${cart.total_discount || 0}`;
    if (!force && key === lastBundleStateKey) return;
    lastBundleStateKey = key;

    capture('bundle_threshold_state', {
      state,
      cart_item_count: itemCount,
      cart_total: money(cart.total_price),
      distance_to_threshold: Math.max(0, CART_THRESHOLD - itemCount),
      bundle_discount_amount: money(cart.total_discount),
    });
  };

  const trackBundleThresholdFromCart = async ({ force = false } = {}) => {
    try {
      const cart = await getCart();
      trackBundleThresholdState(cart, { force });
    } catch (error) {
      console.warn('[nola-posthog] bundle_threshold_state tracking failed', error);
    }
  };

  const trackCheckoutClicked = async (source = 'unknown') => {
    try {
      const cart = await getCart();
      capture('checkout_clicked', {
        cart_total: money(cart.total_price),
        cart_item_count: cart.item_count,
        has_bundle_discount: Number(cart.item_count) >= CART_THRESHOLD,
        discount_amount: money(cart.total_discount),
        currency: cartCurrency(cart),
        source,
      });
    } catch (error) {
      console.warn('[nola-posthog] checkout_clicked tracking failed', error);
    }
  };

  const checkoutSource = (button) => {
    if (button.closest('.cart-drawer, .nola-cart')) return 'drawer';
    if (button.closest('.cart-page, [data-section-id*="cart"], form[action*="/cart"]')) return 'cart_page';
    return 'unknown';
  };

  const initCheckoutTracking = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      if (!button) return;
      trackCheckoutClicked(checkoutSource(button));
    }, true);
  };

  const initBundleTracking = () => {
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-testid="cart-drawer-trigger"], .header-actions__cart-icon')) {
        trackBundleThresholdFromCart({ force: true });
      }
    }, true);

    document.addEventListener('cart:update', (event) => {
      const cart = event.detail?.resource;
      if (cart?.items) trackBundleThresholdState(cart);
      else trackBundleThresholdFromCart();
    });

    document.addEventListener('nola:cart:updated', (event) => {
      if (event.detail?.items) trackBundleThresholdState(event.detail);
    });
  };

  const initProductViewed = () => {
    document.querySelectorAll('script[data-nola-posthog-product]').forEach((script) => {
      try {
        trackProductViewed(JSON.parse(script.textContent || '{}'));
      } catch (error) {
        console.warn('[nola-posthog] product_viewed payload invalid', error);
      }
    });
  };

  const initPdpContentTracking = () => {
    const rows = Array.from(document.querySelectorAll('[data-nola-pdp-accordion]'));
    const twiniHosts = Array.from(document.querySelectorAll('[data-nola-twini-host]'));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          trackPdpAccordionViewed(entry.target);
        });
      }, { threshold: 0.35 });

      rows.forEach((row) => observer.observe(row));

      const twiniObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          trackTwiniViewed(entry.target);
        });
      }, { threshold: 0.35 });

      twiniHosts.forEach((host) => twiniObserver.observe(host));
    } else {
      rows.forEach(trackPdpAccordionViewed);
      twiniHosts.forEach(trackTwiniViewed);
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-nola-pdp-accordion] [data-nola-disclosure-trigger]');
      if (trigger) {
        const row = trigger.closest('[data-nola-pdp-accordion]');
        window.setTimeout(() => trackPdpAccordionToggled(row), 0);
      }

      const twiniHost = event.target.closest('[data-nola-twini-host]');
      if (twiniHost) trackTwiniInteracted(twiniHost, 'click', event.target);
    }, true);

    document.addEventListener('focusin', (event) => {
      const twiniHost = event.target.closest('[data-nola-twini-host]');
      if (twiniHost) trackTwiniInteracted(twiniHost, 'focus', event.target);
    }, true);

    document.addEventListener('submit', (event) => {
      const twiniHost = event.target.closest('[data-nola-twini-host]');
      if (twiniHost) trackTwiniInteracted(twiniHost, 'submit', event.target);
    }, true);
  };

  const initIdentify = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const email = form.querySelector('input[type="email"], input[name="email"], input[name="contact[email]"]')?.value;
      if (email && hasPostHog() && typeof window.posthog.identify === 'function') {
        window.posthog.identify(email, { email });
      }
    }, true);
  };

  window.NolaPostHog = {
    capture,
    getCart,
    trackAddToCart,
    trackBundleThresholdState,
    trackBundleThresholdFromCart,
    trackCheckoutClicked,
    trackProductViewed,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductViewed();
      initCheckoutTracking();
      initBundleTracking();
      initPdpContentTracking();
      initIdentify();
    }, { once: true });
  } else {
    initProductViewed();
    initCheckoutTracking();
    initBundleTracking();
    initPdpContentTracking();
    initIdentify();
  }
})();
